import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  Drawer, 
  List, 
  ListItem, 
  ListItemText, 
  Divider, 
  Button,
  Badge,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import DeleteIcon from '@mui/icons-material/Delete';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import CloseIcon from '@mui/icons-material/Close';
import { useBucket } from '../contexts/BucketContext';

interface AnswerBucketProps {
  drawerWidth?: number;
  hideButton?: boolean;
}

const AnswerBucket: React.FC<AnswerBucketProps> = ({ drawerWidth = 320, hideButton = false }) => {
  // Reference to Phila Markdown instance
  const philaMarkdownRef = useRef<any>(null);

  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // Initialize Phila Markdown when component mounts
  useEffect(() => {
    if (window.PhilaMarkdown) {
      philaMarkdownRef.current = new window.PhilaMarkdown();
    }
  }, []);
  
  const { 
    savedAnswers, 
    removeFromBucket, 
    clearBucket, 
    exportToPDF, 
    exportToWord, 
    copyToClipboard,
    isDrawerOpen,
    toggleDrawer 
  } = useBucket();



  const handleCopyToClipboard = async () => {
    const success = await copyToClipboard();
    if (success) {
      setNotification({
        open: true,
        message: 'Antworten wurden in die Zwischenablage kopiert',
        severity: 'success'
      });
    } else {
      setNotification({
        open: true,
        message: 'Fehler beim Kopieren in die Zwischenablage',
        severity: 'error'
      });
    }
  };

  const handleExportToPDF = () => {
    exportToPDF();
    setNotification({
      open: true,
      message: 'PDF wird heruntergeladen',
      severity: 'success'
    });
  };

  const handleExportToWord = () => {
    exportToWord();
    setNotification({
      open: true,
      message: 'Word-Dokument wird heruntergeladen',
      severity: 'success'
    });
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  return (
    <>
      {!hideButton && (
        <Tooltip title="Antworten-Sammlung">
          <IconButton 
            onClick={toggleDrawer}
            color="primary"
            sx={{ 
              position: 'fixed',
              left: 20,
              bottom: 20,
              backgroundColor: 'white',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              '&:hover': {
                backgroundColor: '#f5f5f5'
              },
              zIndex: 1200
            }}
          >
            <Badge badgeContent={savedAnswers.length} color="primary">
              <BookmarkIcon />
            </Badge>
          </IconButton>
        </Tooltip>
      )}

      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={toggleDrawer}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 2,
          backgroundColor: 'primary.main',
          color: 'white'
        }}>
          <Typography variant="h6">
            Gespeicherte Antworten
          </Typography>
          <IconButton onClick={toggleDrawer} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button 
              variant="outlined" 
              size="small" 
              startIcon={<FileCopyIcon />}
              onClick={handleCopyToClipboard}
              disabled={savedAnswers.length === 0}
              fullWidth
            >
              Kopieren
            </Button>
            <Button 
              variant="outlined" 
              size="small" 
              startIcon={<PictureAsPdfIcon />}
              onClick={handleExportToPDF}
              disabled={savedAnswers.length === 0}
              fullWidth
            >
              PDF
            </Button>
            <Button 
              variant="outlined" 
              size="small" 
              startIcon={<DescriptionIcon />}
              onClick={handleExportToWord}
              disabled={savedAnswers.length === 0}
              fullWidth
            >
              Word
            </Button>
          </Box>
          
          {savedAnswers.length > 0 && (
            <Button 
              variant="text" 
              color="error" 
              size="small" 
              onClick={clearBucket}
              sx={{ mb: 2 }}
            >
              Alle löschen
            </Button>
          )}
        </Box>

        <Divider />

        {savedAnswers.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              Keine gespeicherten Antworten
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Klicke auf das Lesezeichen-Symbol bei einer Antwort, um sie zu speichern
            </Typography>
          </Box>
        ) : (
          <List sx={{ overflow: 'auto', flexGrow: 1 }}>
            {savedAnswers.map((answer) => (
              <React.Fragment key={answer.id}>
                <ListItem
                  secondaryAction={
                    <IconButton 
                      edge="end" 
                      aria-label="delete"
                      onClick={() => removeFromBucket(answer.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {answer.videoInfo.name}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {new Date(answer.savedAt).toLocaleString()}
                        </Typography>
                        <Box 
                          ref={(el) => {
                            if (el && philaMarkdownRef.current) {
                              // Render markdown content using Phila Markdown
                              philaMarkdownRef.current.renderToElement(answer.message.text, el);
                            }
                          }}
                          sx={{ 
                            mt: 1, 
                            maxHeight: '100px', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            '& > *': {
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical'
                            }
                          }}
                        />
                      </>
                    }
                  />
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        )}
      </Drawer>

      <Snackbar 
        open={notification.open} 
        autoHideDuration={4000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

// Add TypeScript declaration for PhilaMarkdown
declare global {
  interface Window {
    PhilaMarkdown: any;
  }
}

export default AnswerBucket;
